// ts-rest contract for the Comply > Breach Register surface.
//
// Phase 4 of /api/db/:table removal (OLD_CODE.html #1). Replaces the page's
// useTable<BreachRegisterEntry>('breach_register') read + four named-action
// dispatches (approve-breach-draft, reject-breach-draft, mark-breach-resolved,
// add-breach-note) with one typed GET + four typed POSTs. Controller gates
// the staff-persona ACL (comply / admin / view).

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { staffActionResponses } from './agent';

const c = initContract();

// Presentation projection of the ontology `Breach.category` enum
// (`InvestmentMandate | Regulatory | Operational | DataProtection | Other`).
// Every value here MUST be reachable from a `CATEGORY_TO_DTO` pre-image — the
// `category-projection.test.ts` reachable-enum guard (ADR-0003) fails the
// build if a value gains no ontology source. The legacy demo vocabulary
// carried `KYC`, `Conduct`, and `Best Execution`; the ontology `Breach`
// models no such distinction, so those values were trimmed rather than
// advertised as states the backend can never produce (#167).
export const breachCategorySchema = z.enum([
    'Investment Restriction',
    'Regulatory',
    'Operational',
    'Data Protection',
    'Other',
]);
export type BreachCategoryDto = z.infer<typeof breachCategorySchema>;

export const breachSeveritySchema = z.enum(['Material', 'Non-material']);
export type BreachSeverityDto = z.infer<typeof breachSeveritySchema>;

export const breachRegisterStatusSchema = z.enum([
    'Open',
    'Under Investigation',
    'Remediation In Progress',
    'Closed',
]);
export type BreachRegisterStatusDto = z.infer<typeof breachRegisterStatusSchema>;

export const breachRegisterEntrySchema = z.object({
    id: z.string(),
    breachID: z.string(),
    date: z.string(),
    category: breachCategorySchema,
    description: z.string(),
    severity: breachSeveritySchema,
    clientsAffected: z.string(),
    rootCause: z.string(),
    status: breachRegisterStatusSchema,
    resolutionDate: z.string().optional().nullable(),
});
export type BreachRegisterEntryDto = z.infer<typeof breachRegisterEntrySchema>;

export const breachRegisterResponseSchema = z.object({
    entries: z.array(breachRegisterEntrySchema),
});
export type BreachRegisterResponse = z.infer<typeof breachRegisterResponseSchema>;

import { errorBody } from '../schemas/common';

export const complyBreachRegisterContract = c.router(
    {
        list: {
            method: 'GET',
            path: '/comply/breach-register',
            responses: { 200: breachRegisterResponseSchema, 403: errorBody },
            summary: 'List committed breach-register entries',
        },
        markResolved: {
            method: 'POST',
            path: '/comply/breaches/:breachId/resolve',
            pathParams: z.object({ breachId: z.string() }),
            // resolutionNote is the officer's real closure reasoning, threaded onto
            // the ResolveBreach Action input (ADR-0003 write-side rule, #167) — not
            // a server constant.
            body: z.object({ resolutionNote: z.string().min(1) }),
            responses: {
                200: z.object({ entry: breachRegisterEntrySchema }),
                400: errorBody,
                403: errorBody,
            },
            summary: 'Mark a breach as resolved (Closed + resolution date stamped)',
        },
        addNote: {
            method: 'POST',
            path: '/comply/breaches/:breachId/notes',
            pathParams: z.object({ breachId: z.string() }),
            body: z.object({ note: z.string() }),
            responses: {
                200: z.object({ entry: breachRegisterEntrySchema }),
                400: errorBody,
                403: errorBody,
            },
            summary: 'Append a timestamped note to a breach entry',
        },

        // -----------------------------------------------------------------
        // AEGIS-Breach materialising-detector endpoints (OLD_CODE.html #2
        // replacement for `approve-breach-draft-finding`,
        // `reject-breach-draft-finding`, `request-revision-breach-draft`).
        // Approve materialises an `breaches` row (ADR-0010).
        // -----------------------------------------------------------------
        approveBreachDraftFinding: {
            method: 'POST',
            path: '/comply/breach-draft-findings/:id/approve',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}).optional(),
            responses: staffActionResponses,
            summary: 'Approve a breach-draft finding (materialises an `breaches` row, ADR-0010).',
        },
        rejectBreachDraftFinding: {
            method: 'POST',
            path: '/comply/breach-draft-findings/:id/reject',
            pathParams: z.object({ id: z.string() }),
            body: z.object({ reason: z.string().optional() }).optional(),
            responses: staffActionResponses,
            summary: 'Reject a breach-draft finding.',
        },
        requestRevisionBreachDraft: {
            method: 'POST',
            path: '/comply/breach-draft-findings/:id/request-revision',
            pathParams: z.object({ id: z.string() }),
            body: z.object({ revisionReason: z.string().min(1) }),
            responses: staffActionResponses,
            summary: 'Request a revision on a breach-draft finding (ADR-0010).',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
